'use client';

import React, { useCallback, useState, useMemo } from 'react';
import { InstantSearch, Configure } from 'react-instantsearch-dom';
import classNames from 'classnames';
import { debounce } from 'lodash';

import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import { searchClient } from 'components/essentials/AlgoliaSearchClient';
import DesktopProductsHeader from 'components/consumer/category-page/components/DesktopProductsHeader';
import {
  ALGOLIA_INDEX_NAME,
  SORT_ORDER_TO_ALGOLIA_INDEX,
  DEFAULT_SORT_ORDER,
} from 'commons/constants';
import IconFilter from 'components/icons/iconFilter';
import { transformInspirationImageUrl } from 'components/consumer/inspiration-photos-grid/search-state/utils';
import CustomerNameWithEmoji from 'components/consumer/CustomerNameWithEmoji';
import useToggleIsSearchOpen from 'global-state/isSearchOpen/useToggleIsSearchOpen';
import MobileAndTabletOnly from 'components/consumer/MobileAndTabletOnly';
import DesktopOnly from 'components/consumer/DesktopOnly';
import DesktopProductListingSidebarContainer from 'components/consumer/ProductListingPage/components/desktop/DesktopProductListingSidebarContainer';
import useIsBrowser from 'hooks/useIsBrowser';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import JBSearchBox from 'components/consumer/consumer-search/components/JBSearchBox';
import {
  ResultHeaderSummary,
  productIndexes,
  CATEGORY_INDEX_MAPPING,
  ChangeToNextPopulatedIndex,
} from '../commons/helpers';

import MobileSearchFilters from './components/MobileSearchFilters';
import SearchDesktopSidebarHeader from './components/SearchDesktopSidebarHeader';
import SearchPagesContent from './components/SearchPagesContent';
import SearchCustomerPhotosContent from './components/SearchCustomerPhotosContent';
import SearchReviewsContent from './components/SearchReviewsContent';
import SearchProductsContent from './components/SearchProductsContent';

import useSearchUrlQueryParams from './hooks/useSearchUrlQueryParams';

const ConsumerAdvancedSearchStatic = () => {
  const isBrowser = useIsBrowser();

  const {
    queryUrlParam,
    categoryUrlParam,
    sortByUrlParam,
    updateUrlParam,
  } = useSearchUrlQueryParams();

  const [sortOrder, setSortOrder] = useState(sortByUrlParam);

  // Determine the initial Algolia index based on category and sort order
  const getIndexForCategoryAndSort = useCallback((category, sort) => {
    if (
      productIndexes?.includes(category) &&
      sort &&
      SORT_ORDER_TO_ALGOLIA_INDEX[sort]
    ) {
      return SORT_ORDER_TO_ALGOLIA_INDEX[sort];
    }
    return category;
  }, []);

  const [algoliaIndexName, setAlgoliaIndexName] = useState(
    getIndexForCategoryAndSort(categoryUrlParam, sortByUrlParam)
  );

  const [searchStateFiltersAndPage, setSearchStateFiltersAndPage] = useState({
    configure: {
      facetFilters: '',
      facets: ['*'],
    },
    hitsPerPage: 12,
    page: 1,
  });

  const [searchStateQuery, updateSearchStateQuery] = useState(queryUrlParam);

  const [searchStateQueryVal, setSearchStateQueryVal] = useState(queryUrlParam);

  const setSearchStateQueryValDebounced = useMemo(
    () =>
      debounce(
        val => val && val?.length > 3 && setSearchStateQueryVal(val),
        1000
      ),
    [setSearchStateQueryVal]
  );

  const setSearchStateQuery = useCallback(
    updatedValue => {
      updateSearchStateQuery(updatedValue);
      if (!isNewConsumerApp || isBrowser) {
        if (updatedValue && updatedValue?.length > 3) {
          updateUrlParam({ q: updatedValue });
        }
        setSearchStateQueryValDebounced(updatedValue);
      }
    },
    [
      updateSearchStateQuery,
      isBrowser,
      updateUrlParam,
      setSearchStateQueryValDebounced,
    ]
  );
  const [isDesktopFiltersOpen, setIsDesktopFiltersOpen] = useState(true);

  const [hoveredValue, setHoveredValue] = useState({
    isHovered: false,
    hoveredOver: null,
  });

  const [expandableOpen, setExpandableOpen] = useState(false);
  const [inactive, setInactive] = useState(false);
  const [invalidProductIds, setInvalidProductIds] = useState([]);

  const searchState = useMemo(
    () => ({
      query: searchStateQueryVal,
      ...(searchStateFiltersAndPage || {}),
    }),
    [searchStateQueryVal, searchStateFiltersAndPage]
  );

  const toggleIsSearchOpen = useToggleIsSearchOpen();

  const toggleReviewOnHover = photoId => {
    const updatedHoveredValue = { ...hoveredValue };

    if (photoId === 'out') {
      updatedHoveredValue.isHovered = false;
      updatedHoveredValue.hoveredOver = null;
    } else {
      updatedHoveredValue.isHovered = !hoveredValue.isHovered;
      updatedHoveredValue.hoveredOver = photoId;
    }

    setHoveredValue(updatedHoveredValue);
  };

  const onIndexChange = newIndex => {
    const updatedSearchFiltersAndPage = { ...searchStateFiltersAndPage };
    updatedSearchFiltersAndPage.page = 1;

    const finalNewIndex =
      newIndex?.includes('price') || newIndex === ALGOLIA_INDEX_NAME.PRODUCTS
        ? newIndex
        : CATEGORY_INDEX_MAPPING[newIndex];

    if (newIndex !== algoliaIndexName) {
      setAlgoliaIndexName(finalNewIndex);
      updateUrlParam({ category: finalNewIndex });
      setExpandableOpen(false);
      setSearchStateFiltersAndPage(updatedSearchFiltersAndPage);
    }
  };

  const onSortOrderChange = useCallback(
    newSortOrder => {
      const updatedSearchFiltersAndPage = { ...searchStateFiltersAndPage };
      updatedSearchFiltersAndPage.page = 1;

      setSortOrder(newSortOrder);

      const newIndex = getIndexForCategoryAndSort(
        categoryUrlParam,
        newSortOrder
      );
      setAlgoliaIndexName(newIndex);

      if (newSortOrder === DEFAULT_SORT_ORDER) {
        updateUrlParam({ sortBy: null });
      } else {
        updateUrlParam({ sortBy: newSortOrder });
      }

      setSearchStateFiltersAndPage(updatedSearchFiltersAndPage);
    },
    [
      searchStateFiltersAndPage,
      categoryUrlParam,
      getIndexForCategoryAndSort,
      updateUrlParam,
    ]
  );

  const updateSearchPage = () => {
    const updatedSearchFiltersAndPage = { ...searchStateFiltersAndPage };
    updatedSearchFiltersAndPage.page += 1;
    setSearchStateFiltersAndPage(updatedSearchFiltersAndPage);
  };

  const loadRest = useCallback(() => {
    setSearchStateFiltersAndPage(prevSearchFiltersAndPage => ({
      ...prevSearchFiltersAndPage,
      hitsPerPage: 1000, // default limit is 1000
    }));
  }, [setSearchStateFiltersAndPage]);

  const setActiveAlgoliaFilter = useCallback(
    (filterAndValue, isActive) => {
      setSearchStateFiltersAndPage(prevState => {
        setSearchStateFiltersAndPage(prev => {
          const existingFilters =
            prev.configure.facetFilters &&
            // Algolia can return facetFilters as a string when there's only one
            // filter applied, so we normalize it to an array
            typeof prev.configure.facetFilters === 'string'
              ? [prev.configure.facetFilters]
              : prev.configure.facetFilters || [];

          if (isActive && existingFilters.includes(filterAndValue)) {
            return prevState; // No change needed
          }

          return {
            ...prevState,
            configure: {
              ...prevState.configure,
              facetFilters: isActive
                ? [...existingFilters, filterAndValue]
                : existingFilters.filter(f => f !== filterAndValue),
            },
          };
        });
      });
    },
    [setSearchStateFiltersAndPage]
  );

  const resetAlgoliaFilters = useCallback(() => {
    setSearchStateFiltersAndPage(prevState => ({
      ...prevState,
      configure: {
        ...prevState.configure,
        facetFilters: [],
      },
    }));
  }, [setSearchStateFiltersAndPage]);

  const handleToggleQuickshipFilter = useCallback(
    shouldBeActive => {
      setSearchStateFiltersAndPage(prevState => {
        setSearchStateFiltersAndPage(prev => {
          const existingFilters =
            prev.configure.facetFilters &&
            typeof prev.configure.facetFilters === 'string'
              ? [prev.configure.facetFilters]
              : prev.configure.facetFilters || [];

          const filterToSet = 'is_in_stock:true';

          return {
            ...prevState,
            configure: {
              ...prevState.configure,
              facetFilters: shouldBeActive
                ? [...existingFilters, filterToSet]
                : existingFilters.filter(f => f !== filterToSet),
            },
          };
        });
      });
    },
    [setSearchStateFiltersAndPage]
  );

  const removeProductIdFromList = productId => {
    if (!productId) {
      return;
    }

    if (!invalidProductIds?.includes(productId)) {
      setInvalidProductIds([...invalidProductIds, parseInt(productId, 10)]);
    }
  };

  const updateInvalidProductIds = updatedInvalidIds => {
    if (updatedInvalidIds?.length !== invalidProductIds?.length) {
      setInvalidProductIds(updatedInvalidIds);
    }
  };

  const getCustomProps = selectedGridItem => {
    if (!selectedGridItem) {
      return {};
    }

    const customerName = `${
      selectedGridItem.customer_first_name
    } ${selectedGridItem.customer_last_name &&
      selectedGridItem.customer_last_name[0]}.`;

    const products = selectedGridItem.products || [];

    return {
      imageProps: {
        src: transformInspirationImageUrl(selectedGridItem.image_link) || '',
        trim: 'auto',
      },
      selectedItemName: (
        <CustomerNameWithEmoji>{customerName}</CustomerNameWithEmoji>
      ),
      selectedItemNameMobile: (
        <span className="CustomerPhotosCustomerNameMobile">{customerName}</span>
      ),
      selectedItemProducts: products,
      selectedItemMaterialList: products.map(p => p.upholstery),
      selectedItemDescription: selectedGridItem.photo_comment || '',
      showDescriptionQuoteMark: true,
    };
  };

  return (
    <div
      className={classNames(
        'relative mb-[100px] lg:z-[1001]',
        '[&_.ais-SearchBox-input]:pl-[30px] [&_.ais-SearchBox-input]:indent:[unset] [&_.ais-SearchBox-input]:text-left',
        '[&_.ais-SearchBox-input::placeholder]:text-left [&_.ais-SearchBox-input::placeholder]:indent:[unset]'
      )}
    >
      <InstantSearch
        searchClient={searchClient}
        indexName={algoliaIndexName}
        searchState={searchState}
      >
        <Configure
          facets={['*']}
          facetFilters=""
          numericFilters={[
            'item_availability<2',
            'visibility>0',
            'visibility<4',
          ]}
        />
        <div className="relative h-auto lg:!z-[1002]">
          <JBSearchBox
            defaultRefinement={searchStateQuery || ''}
            advanced
            setInactive={setInactive}
            toggleSearchOpen={toggleIsSearchOpen}
            index={algoliaIndexName}
            initialQueryValue={queryUrlParam}
            setSearchStateQuery={setSearchStateQuery}
          />
        </div>

        <FixedWidthContainer wide noMobilePadding noTabletPadding>
          {!productIndexes?.includes(algoliaIndexName) && !!searchState.query && (
            <div className="pt-5">
              <DesktopProductsHeader>
                <div className="flex">
                  <ResultHeaderSummary
                    invalidProductIds={invalidProductIds}
                    indexName={algoliaIndexName}
                  />
                </div>

                <div className="items-center flex flex-1 justify-end consumer-page__top-filters">
                  <button
                    type="button"
                    className="text-base font-normal text-gray flex items-center justify-center relative mr-auto ml-4 w-[30px] h-[30px] rounded-[50%] bg-gray-light8 hover:cursor-pointer [&_svg]:relative [&_svg]:w-3 [&_svg]:h-38"
                    onClick={() => {
                      setIsDesktopFiltersOpen(!isDesktopFiltersOpen);
                    }}
                    aria-label="Toggle filters"
                  >
                    <IconFilter />
                  </button>
                </div>
              </DesktopProductsHeader>
            </div>
          )}

          <div className="mt-0 mx-0 mb-8 lg:mb-8 lg:flex">
            <ChangeToNextPopulatedIndex
              currentIndex={algoliaIndexName}
              indexChange={onIndexChange}
            />

            <MobileAndTabletOnly>
              <MobileSearchFilters
                expandableOpen={expandableOpen}
                index={algoliaIndexName}
                onIndexChange={onIndexChange}
                searchState={searchState}
                setExpandableOpen={setExpandableOpen}
              />
            </MobileAndTabletOnly>

            {!productIndexes?.includes(algoliaIndexName) &&
              !!searchState.query && (
                <DesktopOnly>
                  <div className="mt-2">
                    <DesktopProductListingSidebarContainer
                      isOpen={isDesktopFiltersOpen}
                    >
                      <SearchDesktopSidebarHeader
                        index={algoliaIndexName}
                        onIndexChange={onIndexChange}
                        searchState={searchState}
                      />
                    </DesktopProductListingSidebarContainer>
                  </div>
                </DesktopOnly>
              )}

            {!!searchState.query && (
              <div className="w-full mt-0 lg:mt-0">
                <div className="md:mx-[3.333vw] md:my-0 lg:w-full lg:m-0">
                  {!productIndexes?.includes(algoliaIndexName) && (
                    <MobileAndTabletOnly>
                      <div className="lg:hidden">
                        <ResultHeaderSummary />
                      </div>
                    </MobileAndTabletOnly>
                  )}

                  {productIndexes?.includes(algoliaIndexName) && (
                    <SearchProductsContent
                      hitsPerPage={searchStateFiltersAndPage.hitsPerPage}
                      inactive={inactive}
                      indexName={algoliaIndexName}
                      invalidProductIds={invalidProductIds}
                      loadRest={loadRest}
                      onIndexChange={onIndexChange}
                      onSortOrderChange={onSortOrderChange}
                      onToggleQuickshipFilter={handleToggleQuickshipFilter}
                      removeProductIdFromList={removeProductIdFromList}
                      resetAlgoliaFilters={resetAlgoliaFilters}
                      searchClient={searchClient}
                      searchState={searchState}
                      searchStateQuery={searchStateQuery}
                      setActiveAlgoliaFilter={setActiveAlgoliaFilter}
                      setInactive={setInactive}
                      sortOrder={sortOrder}
                      toggleIsSearchOpen={toggleIsSearchOpen}
                      updateInvalidProductIds={updateInvalidProductIds}
                    />
                  )}

                  {algoliaIndexName === ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS && (
                    <SearchCustomerPhotosContent
                      facetFilters={searchState.configure.facetFilters}
                      getCustomProps={getCustomProps}
                      hoveredValue={hoveredValue}
                      indexName={algoliaIndexName}
                      searchState={searchState}
                      searchStateQuery={searchStateQuery}
                      toggleReviewOnHover={toggleReviewOnHover}
                      updateSearchPage={updateSearchPage}
                    />
                  )}

                  {algoliaIndexName === ALGOLIA_INDEX_NAME.REVIEWS && (
                    <SearchReviewsContent
                      indexName={algoliaIndexName}
                      searchClient={searchClient}
                      searchState={searchState}
                      searchStateQuery={searchStateQuery}
                      updateSearchPage={updateSearchPage}
                    />
                  )}

                  {algoliaIndexName === ALGOLIA_INDEX_NAME.STATIC_PAGES && (
                    <SearchPagesContent
                      facetFilters={searchState.configure.facetFilters}
                      indexName={algoliaIndexName}
                      page={searchState.page}
                      query={searchState.query}
                      searchState={searchState}
                      searchStateQuery={searchStateQuery}
                      toggleIsSearchOpen={toggleIsSearchOpen}
                      updateSearchPage={updateSearchPage}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </FixedWidthContainer>
      </InstantSearch>
    </div>
  );
};

const ConsumerAdvancedSearchStaticWrapper = () => {
  const isBrowser = useIsBrowser();

  if (!isNewConsumerApp || isBrowser) {
    return <ConsumerAdvancedSearchStatic />;
  }
  return <div />; // null return is causing issues
};

export default ConsumerAdvancedSearchStaticWrapper;
